import React, { useState, useRef, useEffect, forwardRef } from 'react';
import {
  TextInput,
  Text,
  Animated,
  Easing,
  Platform,
} from 'react-native';
import { ls } from './styles';
import FadeInAnimation from '../FadinAnimation/FadinAnimation';
import { ColorTokens } from '@voltmoney/schema';
import { ThemedPressable, ThemedView } from '@app/theme';
import { ThemedText } from '@app/theme/Components';
import { useDarkMode } from '@app/utils';



type InputProps = {
  height?: number; // Height of the input
  placeHolder?: string | null; // Placeholder text for the input
  maxLength?: number | null; // Maximum length of the input
  keyboardType?: 'default' | 'numeric' | 'email-address' | 'phone-pad' | string; // Keyboard type for the input
  value?: string | null; // Value of the input
  onChangeText?: (text: string) => void; // Function to handle text change
  onChange?: (event: any) => void; // Function to handle change event
  onFocus?: () => void; // Function to handle focus event
  onBlur?: () => void; // Function to handle blur event
  showLeftAccessory?: boolean; // Boolean to show left accessory
  leftAccessory?: () => React.ReactNode; // Function to render left accessory
  showRightAccessory?: boolean; // Boolean to show right accessory
  rightAccessory?: () => React.ReactNode; // Function to render right accessory
  overRideContainerStyles?: object; // Styles to override container styles
  overRideInputStyles?: object; // Styles to override input styles
  overRideLabelStyles?: object; // Styles to override label styles
  showLabel?: boolean; // Boolean to show label
  label?: string; // Label text
  showErrorMessage?: boolean; // Boolean to show error message
  showErrorMessageInFocus?: boolean; // Boolean to show error message in focus
  hasError?: string; // Error message text
  showAnimation?: boolean; // Boolean to show animation
  autoFocus?: boolean; // Boolean to auto focus the input
  autoCapitalize?: 'none' | 'sentences' | 'words' | 'characters'; // Auto capitalize type`
  multiline?: boolean; // Boolean to enable multiline
  numberOfLines?: number; // Number of lines for multiline input
  secureTextEntry?: boolean; // Boolean to enable secure text entry
};


// InputText component definition
const InputText = forwardRef(
  (
    {
      height = 48,
      placeHolder = null, // Placeholder text for the input
      maxLength = null, // Maximum length of the input
      keyboardType = 'default', // Keyboard type for the input
      value = null, // Value of the input
      onChangeText = () => { }, // Function to handle text change
      onChange = () => { }, // Function to handle change event
      onFocus = () => { }, // Function to handle focus event
      onBlur = () => { }, // Function to handle blur event
      showLeftAccessory, // Boolean to show left accessory
      leftAccessory, // Function to render left accessory
      showRightAccessory, // Boolean to show right accessory
      rightAccessory, // Function to render right accessory
      overRideContainerStyles, // Styles to override container styles
      overRideInputStyles, // Styles to override input styles
      overRideLabelStyles, // Styles to override label styles
      showLabel, // Boolean to show label
      label, // Label text
      showErrorMessage = false, // Boolean to show error message
      showErrorMessageInFocus=false,
      hasError = '', // Error message text
      showAnimation = false, // Boolean to show animation
      autoFocus = false, // Boolean to auto focus the input
      autoCapitalize = 'none', // Auto capitalize type
      multiline = false, // Boolean to enable multiline
      numberOfLines = 1, // Number of lines for multiline input
      secureTextEntry = false, // Boolean to enable secure text entry
      
    }: InputProps,
    ref,
  ) => {
    // State to manage focus state
    const [isFocused, setIsFocused] = useState(false);
    // State to manage blur state
    const [isBlurred, setIsBlurred] = useState(false);
    // Animated value for focus animation
    const focusAnim = useRef(new Animated.Value(0)).current;
    const isDarkMode = useDarkMode();
    // Effect to handle focus animation
    useEffect(() => {
      if (showAnimation) {
        Animated.timing(focusAnim, {
          toValue: isFocused || !!value ? 1 : 0,
          duration: 150,
          easing: Easing.bezier(0.4, 0, 0.2, 1),
          useNativeDriver: true,
        }).start();
      } else {
        Animated.timing(focusAnim, {
          toValue: 1,
          duration: 0,
          easing: Easing.bezier(0.4, 0, 0.2, 1),
          useNativeDriver: true,
        }).start();
      }
    }, [focusAnim, isFocused, value, showAnimation]);

    // Determine the default class based on focus and error state
    const defaultClass = isFocused
      ? (showErrorMessageInFocus && Boolean(hasError) && ls.inputError) || ls.inputFocused
      : isBlurred && Boolean(hasError) && ls.inputError;

    // Determine if the error message should be displayed
    const displayError = showErrorMessageInFocus ? Boolean(hasError):Boolean(hasError) && !isFocused && isBlurred;

    // Determine if the placeholder should be shown
    const showPlaceholder = showAnimation ? isFocused : true;

    return (
      <ThemedView style={[overRideContainerStyles]}>
        <ThemedPressable
        height={height}
        backgroundColor='bg-surface-primary'
        flexDirection='row'
        alignItems='center'
        justifyContent='space-between'
        borderColor={Boolean(hasError) ? 'border-error' : isFocused ? 'border-focus': 'border-form'}
        borderWidth={1}
        borderRadius='xs'
        mt='sm'
        py="md"
        px="lg"
        width={"100%"}
        {... multiline && {height: 100}}
        hitSlop={{top: 10, bottom: 10, left: 10, right: 10}}
        onPress={() => {
          ref?.current?.focus();
        }}>
          {showLabel && (
            <Animated.View
              style={[
                ls.labelContainer,
                {
                  zIndex: value ? 1 : -1,
                  marginLeft: Platform.OS !== 'web' ? 15 : 0,
                },
                {
                  transform: [
                    {
                      scale:focusAnim.interpolate({
                        inputRange: [0, 1],
                        outputRange: [1, 0.75],
                      }),
                    },
                    {
                      translateY: focusAnim.interpolate({
                        inputRange: [0, 1],
                        outputRange: [0,  -(33+Math.abs((height-48)/2))],
                      }),
                    },
                    {
                      translateX: focusAnim.interpolate({
                        inputRange: [0, 1],
                        outputRange: [showLeftAccessory ? 26 : 0, -10],
                      }),
                    },
                  ],
                },
              ]}>
              <ThemedView backgroundColor='bg-surface-primary' px="xs" >
                <Text style={[ls.label,isDarkMode && { color:  ColorTokens.White }, defaultClass]}>{label}</Text>
              </ThemedView>
            </Animated.View>
          )}
          {showLeftAccessory && leftAccessory()}
          <TextInput
            placeholderTextColor={'#98a2b3'}
            autoCapitalize={autoCapitalize}
            ref={ref}
            autoCorrect={false}
            style={[ls.input, overRideInputStyles, { color: Platform.OS !== 'web' ? 'black' : isDarkMode ? ColorTokens.White : ColorTokens.Grey_Smoke }]}
            keyboardType={keyboardType}
            autoFocus={autoFocus}
            maxLength={maxLength}
            placeholder={showPlaceholder ? placeHolder : ''}
            value={value}
            nativeID={ label || placeHolder}
            multiline={multiline}
            numberOfLines={numberOfLines}
            id={label || placeHolder}
            onChangeText={onChangeText}
            onChange={onChange}
            onFocus={() => {
              setIsFocused(true);
              setIsBlurred(false);
              if (onFocus) {
                onFocus();
              }
            }}
            onBlur={() => {
              setIsFocused(false);
              setIsBlurred(true);
              if (onBlur) {
                onBlur();
              }
            }}
          />
          {showRightAccessory && rightAccessory()}
        </ThemedPressable>
        {showErrorMessage && (
          <ThemedView height={18} my='sm'>
            {displayError && (
              <FadeInAnimation delay={100} duration={500}>
                <ThemedText variant='b4r' color='text-error'>
                  {displayError ? hasError : ''}
                </ThemedText>
              </FadeInAnimation>
            )}
          </ThemedView>
        )}
      </ThemedView>
    );
  },
);

export default InputText;
